import { useState, useEffect } from 'react';
import { Modal, Card, Radio, Button, Spin, Alert, Typography, Space, Tag } from 'antd';
import { ShoppingCartOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { productsApi } from '../services/api';

const { Title, Text } = Typography;

interface SKU {
  id: string;
  sku_id: string;
  period_type: 'DAY' | 'MONTH' | 'YEAR';
  period: number;
  price: string;
  price_currency: string;
  price_amount: number;
}

interface Product {
  id: string;
  spu_key: string;
  spu_name: string;
  spu_desc: string;
  storage_mb: number;
  sku_list: SKU[];
}

interface UpgradeModalProps {
  visible: boolean;
  onClose: () => void;
  colors: {
    primary: string;
    text: string;
    textMuted: string;
    border: string;
    surface: string;
    success: string;
  };
}

export default function UpgradeModal({ visible, onClose, colors }: UpgradeModalProps) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [selectedSKU, setSelectedSKU] = useState<string | null>(null);
  const [purchaseType, setPurchaseType] = useState<'renew' | 'expand'>('renew');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (visible) {
      fetchProducts();
    }
  }, [visible]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await productsApi.getProducts() as unknown as { success: boolean; data: { product_list: Product[] } };
      if (response.success) {
        setProducts(response.data.product_list);
        // 默认选中第一个产品的第一个SKU
        if (response.data.product_list.length > 0) {
          const firstProduct = response.data.product_list[0];
          setSelectedProduct(firstProduct.spu_key);
          if (firstProduct.sku_list.length > 0) {
            setSelectedSKU(firstProduct.sku_list[0].sku_id);
          }
        }
      }
    } catch (err) {
      setError('加载产品信息失败');
    } finally {
      setLoading(false);
    }
  };

  const handleProductSelect = (spuKey: string) => {
    setSelectedProduct(spuKey);
    const product = products.find(p => p.spu_key === spuKey);
    if (product && product.sku_list.length > 0) {
      setSelectedSKU(product.sku_list[0].sku_id);
    }
  };

  const handleSubmit = async () => {
    if (!selectedSKU) return;
    
    try {
      setSubmitting(true);
      // 调用创建订阅 API
      const response = await productsApi.createSubscription(selectedSKU, purchaseType) as unknown as { success: boolean; data?: { paymentUrl?: string; orderNo?: string } };
      console.log('Create subscription response:', response);
      
      if (response.success && response.data?.paymentUrl) {
        // 跳转到支付页面（新窗口打开）
        console.log('Opening payment URL in new window:', response.data.paymentUrl);
        window.open(response.data.paymentUrl, '_blank');
        onClose();
      } else {
        console.error('Invalid response:', response);
        setError('创建订单失败：' + (response.data ? JSON.stringify(response.data) : '未知错误'));
      }
    } catch (err: any) {
      console.error('Create subscription error:', err);
      setError('创建订单失败：' + (err.message || '请稍后重试'));
    } finally {
      setSubmitting(false);
    }
  };

  const getSelectedProduct = () => products.find(p => p.spu_key === selectedProduct);
  const getSelectedSKU = () => {
    const product = getSelectedProduct();
    return product?.sku_list.find(s => s.sku_id === selectedSKU);
  };

  // 从 JSON 字符串中获取指定 key 的值（支持双重转义的 JSON）
  const getJsonValue = (jsonStr: string, key: string, defaultValue: string = ''): string => {
    try {
      // 第一次解析：处理可能的字符串转义
      let parsed: string | Record<string, string> = jsonStr;
      if (typeof jsonStr === 'string' && jsonStr.startsWith('"')) {
        try {
          parsed = JSON.parse(jsonStr) as string;
        } catch {
          // 如果第一次解析失败，直接使用原字符串
        }
      }
      
      // 第二次解析：如果是字符串再解析一次
      if (typeof parsed === 'string' && parsed.startsWith('{')) {
        parsed = JSON.parse(parsed) as Record<string, string>;
      }
      
      // 使用类型断言确保安全访问
      const obj = parsed as Record<string, string>;
      return obj[key] || defaultValue;
    } catch {
      return defaultValue;
    }
  };

  // 格式化价格 - 金额单位为美分，需转换为元
  const formatPrice = (amount: number) => {
    return `$${(amount / 100).toFixed(2)}`;
  };



  const getPeriodText = (sku: SKU) => {
    if (sku.period_type === 'DAY') return `${sku.period}天`;
    if (sku.period_type === 'MONTH') return `${sku.period}个月`;
    if (sku.period_type === 'YEAR') return `${sku.period}年`;
    return '';
  };

  return (
    <Modal
      title={
        <Space>
          <ShoppingCartOutlined style={{ color: colors.primary, fontSize: 20 }} />
          <span style={{ color: colors.text, fontWeight: 600 }}>升级存储空间</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={700}
      footer={null}
      styles={{
        body: { background: colors.surface }
      }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: colors.textMuted }}>加载产品信息...</p>
        </div>
      ) : error ? (
        <Alert
          message={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={fetchProducts}>重试</Button>
          }
        />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* 购买类型选择 */}
          <div>
            <Text strong style={{ color: colors.text, fontSize: 16, display: 'block', marginBottom: 12 }}>
              购买类型
            </Text>
            <Radio.Group
              value={purchaseType}
              onChange={(e) => setPurchaseType(e.target.value)}
              style={{ width: '100%' }}
            >
              <Space direction="horizontal" style={{ width: '100%', display: 'flex' }} size="middle">
                <Card
                  hoverable
                  style={{
                    flex: 1,
                    borderColor: purchaseType === 'renew' ? colors.primary : colors.border,
                    background: purchaseType === 'renew' ? `${colors.primary}08` : colors.surface,
                  }}
                  bodyStyle={{ padding: '16px' }}
                  onClick={() => setPurchaseType('renew')}
                >
                  <Radio value="renew" style={{ width: '100%' }}>
                    <Space direction="vertical" style={{ marginLeft: 8 }} size="small">
                      <Text strong style={{ color: colors.text, fontSize: 16 }}>
                        续费
                      </Text>
                      <Text style={{ color: colors.textMuted, fontSize: 14 }}>
                        延长当前订阅有效期
                      </Text>
                    </Space>
                  </Radio>
                </Card>
                <Card
                  hoverable
                  style={{
                    flex: 1,
                    borderColor: purchaseType === 'expand' ? colors.primary : colors.border,
                    background: purchaseType === 'expand' ? `${colors.primary}08` : colors.surface,
                  }}
                  bodyStyle={{ padding: '16px' }}
                  onClick={() => setPurchaseType('expand')}
                >
                  <Radio value="expand" style={{ width: '100%' }}>
                    <Space direction="vertical" style={{ marginLeft: 8 }} size="small">
                      <Text strong style={{ color: colors.text, fontSize: 16 }}>
                        实时扩充容量
                      </Text>
                      <Text style={{ color: colors.textMuted, fontSize: 14 }}>
                        立即增加存储空间
                      </Text>
                    </Space>
                  </Radio>
                </Card>
              </Space>
            </Radio.Group>
          </div>

          {/* 产品选择 */}
          <div>
            <Text strong style={{ color: colors.text, fontSize: 16, display: 'block', marginBottom: 12 }}>
              选择套餐
            </Text>
            <Radio.Group
              value={selectedProduct}
              onChange={(e) => handleProductSelect(e.target.value)}
              style={{ width: '100%' }}
            >
              <Space direction="horizontal" style={{ width: '100%', display: 'flex' }} size="middle">
                {products.map((product) => (
                  <Card
                    key={product.spu_key}
                    hoverable
                    style={{
                      flex: 1,
                      borderColor: selectedProduct === product.spu_key ? colors.primary : colors.border,
                      background: selectedProduct === product.spu_key ? `${colors.primary}08` : colors.surface,
                    }}
                    bodyStyle={{ padding: '16px' }}
                    onClick={() => handleProductSelect(product.spu_key)}
                  >
                    <Radio value={product.spu_key} style={{ width: '100%' }}>
                      <Space direction="vertical" style={{ marginLeft: 8 }} size="small">
                        <Space>
                          <Text strong style={{ color: colors.text, fontSize: 16 }}>
                            {getJsonValue(product.spu_name, 'zh-hans-cn', getJsonValue(product.spu_name, 'en', product.spu_key))}
                          </Text>
                          <Tag color={colors.primary}>{(product.storage_mb / 1024).toFixed(0)}GB</Tag>
                        </Space>
                      </Space>
                    </Radio>
                  </Card>
                ))}
              </Space>
            </Radio.Group>
          </div>

          {/* 周期选择 */}
          {getSelectedProduct() && (
            <div>
              <Text strong style={{ color: colors.text, fontSize: 16, display: 'block', marginBottom: 12 }}>
                选择周期
              </Text>
              <Radio.Group
                value={selectedSKU}
                onChange={(e) => setSelectedSKU(e.target.value)}
                style={{ width: '100%' }}
              >
                <Space direction="horizontal" style={{ width: '100%', display: 'flex' }} size="middle">
                  {getSelectedProduct()?.sku_list.map((sku) => (
                    <Card
                      key={sku.sku_id}
                      hoverable
                      style={{
                        flex: 1,
                        borderColor: selectedSKU === sku.sku_id ? colors.primary : colors.border,
                        background: selectedSKU === sku.sku_id ? `${colors.primary}08` : colors.surface,
                      }}
                      bodyStyle={{ padding: '16px' }}
                      onClick={() => setSelectedSKU(sku.sku_id)}
                    >
                      <Radio value={sku.sku_id} style={{ width: '100%' }}>
                        <Space direction="vertical" style={{ marginLeft: 8 }} size="small">
                          <Text strong style={{ color: colors.text, fontSize: 16 }}>
                            {getPeriodText(sku)}
                          </Text>
                          <Text style={{ color: colors.primary, fontSize: 18, fontWeight: 700 }}>
                            {formatPrice(sku.price_amount)}
                          </Text>
                        </Space>
                      </Radio>
                    </Card>
                  ))}
                </Space>
              </Radio.Group>
            </div>
          )}

          {/* 结算信息 */}
          {getSelectedSKU() && (
            <div style={{ 
              background: `${colors.primary}08`, 
              padding: '20px', 
              borderRadius: '8px',
              border: `1px solid ${colors.border}`
            }}>
              <Title level={5} style={{ color: colors.text, marginBottom: 16 }}>
                订单结算
              </Title>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text style={{ color: colors.textMuted }}>购买类型</Text>
                  <Text style={{ color: colors.text }}>
                    {purchaseType === 'renew' ? '续费' : '实时扩充容量'}
                  </Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text style={{ color: colors.textMuted }}>套餐</Text>
                  <Text style={{ color: colors.text }}>
                    {getJsonValue(getSelectedProduct()?.spu_name || '{}', 'zh-hans-cn', getJsonValue(getSelectedProduct()?.spu_name || '{}', 'en', ''))}
                  </Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text style={{ color: colors.textMuted }}>周期</Text>
                  <Text style={{ color: colors.text }}>{getPeriodText(getSelectedSKU()!)}</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text strong style={{ color: colors.text, fontSize: 16 }}>应付金额</Text>
                  <Text strong style={{ color: colors.primary, fontSize: 24 }}>
                    {formatPrice(getSelectedSKU()!.price_amount)}
                  </Text>
                </div>
              </Space>
            </div>
          )}

          {/* 操作按钮 */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <Button onClick={onClose} size="large">
              取消
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<CheckCircleOutlined />}
              loading={submitting}
              disabled={!selectedSKU}
              onClick={handleSubmit}
              style={{
                background: colors.primary,
                minWidth: 140,
              }}
            >
              去付款
            </Button>
          </div>
        </Space>
      )}
    </Modal>
  );
}
